import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './Home.css';
import backgroundVideo from '../Assets/background.mp4';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay },
  }),
};

const Home = () => {
  return (
    <div className="home-container">
      <Navbar />

      {/* ---------- Hero Section ---------- */}
      <header className="hero-section">

        <video autoPlay loop muted playsInline className="hero-video">
          <source src={backgroundVideo} type="video/mp4" />
        </video>


        <motion.div
          className="hero-content"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.4 }}
        >
          <h1>Advancing AI Research for Defense, Healthcare & Cybersecurity</h1>
          <p>
            Securing Defense. Advancing Healthcare. Defending the Digital World.
          </p>
          <div className="hero-cta">
            <Link to="/solutions" className="cta-button">
              Explore Our Solutions
            </Link>

            <Link to="/contact" className="outline-button">
              Contact Us
            </Link>
          </div>

          <div className="compliance-badges">
            {['DISA STIG', 'NIST 800-53', 'HIPAA'].map((badge) => (
              <motion.span
                key={badge}
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.3 }}
              >
                {badge}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </header>


      {/* ---------- Why Us ---------- */}
      <div className="security-bg-group">

        <section className="why-us-section">
          <motion.div
            className="section-content"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.35 }}        >
            <h1 className='why-us-head'>Why Choose Sapiencia.AI?</h1>

            <div className="why-us-cards">
              {[
                {
                  title: 'Military-Grade Security',
                  desc: 'Built from the ground up with DoD-level security protocols and compliance frameworks to protect the most sensitive data.',
                  className: 'security-icon',
                },
                {
                  title: 'Rapid Deployment',
                  desc: 'Our solutions can be deployed in secure, air-gapped environments with minimal infrastructure requirements, even at the tactical edge.',
                  className: 'deployment-icon',
                },
                {
                  title: 'Built-in Compliance',
                  desc: 'Solutions that meet HIPAA, DISA STIG, FedRAMP, and other critical compliance requirements out of the box.',
                  className: 'compliance-icon',
                },
              ].map((card, idx) => (
                <motion.div
                  key={card.title}
                  className="why-us-card"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7, delay: idx * 0.2 }}
                  viewport={{ once: false, amount: 0.35 }}
                >
                  <div className={`why-us-icon ${card.className}`}></div>
                  <h3>{card.title}</h3>
                  <p>{card.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        <motion.div
          className="section-divider"
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: false, amount: 0.8 }}
        />

        {/* ---------- Security Standards ---------- */}
        <section className="security-standards">
          <motion.div
            className="section-content"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.35 }}
            custom={0.2}
          >
            <h2>Built to Meet the Most Demanding Security Standards</h2>
            <p>
              Sapiencia.AI is designed from the ground up to align with the most rigorous federal
              and defense security frameworks — engineered for deployment across unclassified and
              classified environments as we scale alongside our customers.
            </p>
            <div className="standards-list">
              {['DISA STIG', 'NIST 800-53', 'HIPAA', 'Section 508', 'FIPS 140-2', 'CSF', 'FISMA'].map((std) => (
                <motion.span key={std} whileHover={{ scale: 1.1, y: -5 }} transition={{ duration: 0.3 }}>{std}</motion.span>
              ))}
            </div>
          </motion.div>
        </section>
      </div>


      <Footer />
    </div>
  );
};

export default Home;